import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";
import { Loader2, Stethoscope, MapPin, Video, Users, Clock } from "lucide-react";
import { UniHeader, fmtSessionRange, usd } from "./_lmsShared";

export default function UniversityResidency() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await universityApi.eventsList({ event_type: "clinical_residency", upcoming_only: 0 });
        setEvents(r.data?.items || []);
      } catch (e) {
        toast.error(formatApiError(e, "Could not load residency sessions"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const inPerson = events.filter((e) => e.track === "in_person");
  const online = events.filter((e) => e.track === "online");

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Clinical Residency" back="/university" />
      <main className="max-w-6xl mx-auto px-6 py-10" data-testid="uni-residency">
        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500 mb-1">
          <Stethoscope className="w-3.5 h-3.5" />Clinical Trichology Residency
        </div>
        <h1 className="font-display text-4xl text-slate-900">Earn the Clinical Holistic Trichology Practitioner credential</h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Choose the residency track that fits your practice. Both meet the residency completion requirement and culminate in the Clinical Holistic Trichology Practitioner credential.
        </p>

        {loading ? (
          <div className="mt-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading sessions…</div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <TrackCard title="In-Person Residency" Icon={MapPin} tone="amber" items={inPerson} bullets={[
              "2-Day Intensive",
              "Clinical Kit included",
              "Hands-On Training",
              "Live Mentorship",
            ]} />
            <TrackCard title="Online Residency" Icon={Video} tone="purple" items={online} bullets={[
              "Case-Based Learning",
              "Virtual Mentorship",
              "Protocol Reviews",
              "Clinical Evaluations",
            ]} />
          </div>
        )}
      </main>
    </div>
  );
}

function TrackCard({ title, Icon, tone, items, bullets }) {
  const ring = tone === "amber" ? "border-amber-200" : "border-purple-200";
  const tag  = tone === "amber" ? "text-amber-900" : "text-purple-900";
  return (
    <section className={`rounded-2xl border ${ring} bg-white p-6`}>
      <div className={`flex items-center gap-2 text-xs uppercase tracking-wide ${tag}`}>
        <Icon className="w-3.5 h-3.5" />{title}
      </div>
      <ul className="mt-3 space-y-1 text-sm text-slate-700">
        {bullets.map((b, i) => <li key={i} className="flex items-center gap-2"><span className={`inline-block w-1.5 h-1.5 rounded-full ${tone === "amber" ? "bg-amber-700" : "bg-purple-900"}`} />{b}</li>)}
      </ul>
      <div className="mt-5 space-y-3">
        {items.length === 0 ? (
          <div className="text-xs text-slate-500 rounded-xl border border-slate-100 p-3">No upcoming sessions yet.</div>
        ) : items.map((e) => (
          <Link to={`/university/events/${e.id}`} key={e.id} className="block rounded-xl border border-slate-100 p-3 hover:border-purple-300 transition" data-testid={`uni-residency-card-${e.id}`}>
            <div className="font-medium text-slate-900 text-sm leading-snug">{e.title}</div>
            <div className="mt-1 text-xs text-slate-500" data-testid={`uni-residency-when-${e.id}`}>{fmtSessionRange(e)}</div>
            <div className="mt-2 flex items-center justify-between text-xs text-slate-700">
              <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{e.seats_remaining == null ? "Open" : `${e.seats_remaining} seats left`}</span>
              <span className="font-medium text-slate-900">{usd(e.price_usd)}</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
